import * as React from 'react';
import { FOCUSABLE_SELECTOR } from '../../utils/focusable';
import { useLatestRef } from '../../hooks/use-latest-ref';

/** Both attributes are written by the cells; together they address one cell in the grid. */
export const GRID_ROW_ATTRIBUTE = 'data-grid-row';
export const GRID_COLUMN_ATTRIBUTE = 'data-grid-col';

/**
 * Which table instance owns the cell. Inner tables restart their coordinates at 0,0 while still sitting
 * inside the outer scroller, so without this an outer grid resolves an inner cell as one of its own.
 */
export const GRID_OWNER_ATTRIBUTE = 'data-grid-owner';

/** A full-width detail cell: a panel of controls rather than one control. See `activate`. */
export const GRID_DETAIL_ATTRIBUTE = 'data-grid-detail';

const cellSelector = (ownerId: string): string =>
    `[${GRID_OWNER_ATTRIBUTE}="${ownerId}"][${GRID_ROW_ATTRIBUTE}][${GRID_COLUMN_ATTRIBUTE}]`;

/** Any grid cell, whoever owns it. */
const ANY_CELL_SELECTOR = `[${GRID_OWNER_ATTRIBUTE}][${GRID_ROW_ATTRIBUTE}][${GRID_COLUMN_ATTRIBUTE}]`;

/** Every cell of one row of one grid. DOM order is column order, so the match set is indexable. */
const rowSelector = (ownerId: string, row: number): string =>
    `[${GRID_OWNER_ATTRIBUTE}="${ownerId}"][${GRID_ROW_ATTRIBUTE}="${row}"][${GRID_COLUMN_ATTRIBUTE}]`;

/** Keys that state a column. Only these, plus the pointer and Tab, may rewrite the desired column. */
const COLUMN_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End']);

/** Only used to size a Page Up / Down jump when the real row height cannot be measured (jsdom, or pre-paint). */
const FALLBACK_PAGE_ROWS = 10;

/** Enter auto-activates these popup openers; plain buttons (e.g. delete) are only focused. */
const OPENER_SELECTOR = '[aria-haspopup], [aria-expanded], summary';

export interface GridCursor {
    /** Header rows occupy `0 .. headerRowCount - 1`; body row `n` is at `headerRowCount + n`. */
    row: number;
    col: number;
}

const readCursor = (cell: HTMLElement): GridCursor => ({
    row: Number(cell.getAttribute(GRID_ROW_ATTRIBUTE)),
    col: Number(cell.getAttribute(GRID_COLUMN_ATTRIBUTE)),
});

export interface UseGridNavigationOptions {
    enabled: boolean;
    scrollRef: React.RefObject<HTMLElement>;
    /** Identifies this table's own cells. Must match the `gridOwner` the cells render. */
    ownerId: string;
    headerRowCount: number;
    /**
     * GRID rows in the body, NOT records: a rendered detail row occupies one of its own. Feeding a record
     * count here caps every clamp one row short for each open detail row.
     */
    bodyGridRowCount: number;
    columnCount: number;
    /** Whether a row absent from the DOM can still mount, i.e. whether waiting for one is ever justified. */
    isVirtualised: boolean;
    /** Body GRID row -> record index, for `scrollToIndex`. Omitted means the two spaces coincide. */
    recordIndexByGridRow?: readonly number[];
    /** Body-record index. Only called when the target row is not currently mounted. */
    scrollToIndex: (index: number) => void;
}

export interface GridNavigation {
    enabled: boolean;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    onFocusCapture: (event: React.FocusEvent<HTMLElement>) => void;
}

/**
 * Roving-tabindex cell navigation for `role="grid"` tables (ARIA APG grid pattern).
 *
 * The tab stop is moved by writing `tabindex` straight to the DOM rather than by re-rendering. Cells always
 * render `tabIndex={-1}` and exactly one is promoted here, which keeps an arrow key press at two DOM writes
 * instead of a re-render of every row in a non-virtualised table.
 *
 * Tab is deliberately never intercepted, so the grid is always escapable (WCAG 2.1.2). Keys are handled only
 * while the CELL itself holds focus; once focus is inside a cell's control every key except Escape belongs to
 * that control.
 *
 * Rows are not all the same width (a detail row is one full-width cell), so two spaces are kept apart: a
 * cursor is clamped against the row it lands in, while the DESIRED column is remembered in table-column space
 * and only a deliberate column change rewrites it. Escape belongs to the NEAREST cell, which may be a nested
 * grid's rather than this one's.
 */
export const useGridNavigation = (options: UseGridNavigationOptions): GridNavigation => {
    const { enabled, scrollRef, ownerId } = options;
    const selector = React.useMemo(() => cellSelector(ownerId), [ownerId]);

    // the counts are read through here rather than destructured, so the handlers below stay stable across
    // every data change while still seeing the current row and column totals
    const latest = useLatestRef(options);
    const cursorRef = React.useRef<GridCursor>({ row: 0, col: 0 });
    const tabStopRef = React.useRef<HTMLElement | null>(null);

    /** The column the user last ASKED for, so a narrow row cannot swallow it. */
    const desiredColumnRef = React.useRef(0);

    /** True while this hook is moving focus itself, so its own focus event cannot rewrite the desired column. */
    const isMovingRef = React.useRef(false);

    /** Set when a move targets a row the virtualiser has not mounted yet; resolved by the effect below. */
    const pendingRef = React.useRef<GridCursor | null>(null);

    const findCell = React.useCallback(
        (cursor: GridCursor): HTMLElement | null => {
            const scroller = scrollRef.current;

            if (scroller === null) {
                return null;
            }

            const exact = scroller.querySelector<HTMLElement>(
                `${rowSelector(ownerId, cursor.row)}[${GRID_COLUMN_ATTRIBUTE}="${cursor.col}"]`,
            );

            if (exact !== null) {
                return exact;
            }

            // a short row (a detail row's single cell, a grouped header row) takes the move on its last cell
            // rather than resolving to nothing, which would strand the pending-move ref
            const cells = scroller.querySelectorAll<HTMLElement>(rowSelector(ownerId, cursor.row));

            return cells.length === 0 ? null : cells[Math.min(Math.max(cursor.col, 0), cells.length - 1)];
        },
        [scrollRef, ownerId],
    );

    /** Focus something this hook chose, without letting the resulting focus event restate the desired column. */
    const focusTarget = React.useCallback((target: HTMLElement): void => {
        isMovingRef.current = true;

        try {
            target.focus();
        } finally {
            isMovingRef.current = false;
        }
    }, []);

    const applyTabStop = React.useCallback((): HTMLElement | null => {
        const cursorCell = findCell(cursorRef.current);

        // The cursor's cell can be unmounted (the virtual window scrolled past it while focus was outside
        // the table). Promote the first rendered cell instead, so the grid never drops out of the Tab
        // order; the cursor itself stays put, so scrolling back restores the real position.
        const cell = cursorCell ?? scrollRef.current?.querySelector<HTMLElement>(selector) ?? null;

        if (cell !== tabStopRef.current) {
            tabStopRef.current?.setAttribute('tabindex', '-1');
            cell?.setAttribute('tabindex', '0');
            tabStopRef.current = cell;
        }

        // the CURSOR cell, not the promoted one: the pending-move effect must not mistake the fallback for
        // its scroll target having mounted
        return cursorCell;
    }, [findCell, scrollRef, selector]);

    // No dependency array: the tab stop has to survive every commit, including the ones that replace the
    // cell element entirely (a sort, a filter, the virtual window scrolling).
    React.useLayoutEffect(() => {
        if (!enabled) {
            return;
        }

        const pending = pendingRef.current;

        if (pending === null) {
            applyTabStop();
            return;
        }

        cursorRef.current = pending;

        const cell = applyTabStop();

        // still unmounted: the virtualiser has not caught up, so wait for the commit that renders it
        if (cell === null) {
            return;
        }

        pendingRef.current = null;
        cursorRef.current = readCursor(cell);

        // never steal: focus comes back only when it is still this grid's, or the browser dropped it
        const active = document.activeElement;

        if (active === null || active === document.body || scrollRef.current?.contains(active) === true) {
            focusTarget(cell);
        }
    });

    const moveTo = React.useCallback(
        (next: GridCursor, statesColumn: boolean): void => {
            const current = latest.current;
            const totalRows = current.headerRowCount + current.bodyGridRowCount;

            const clamped: GridCursor = {
                row: Math.max(0, Math.min(next.row, totalRows - 1)),
                col: Math.max(0, Math.min(next.col, current.columnCount - 1)),
            };

            const cell = findCell(clamped);

            if (cell !== null) {
                const landed = readCursor(cell);

                // a short row absorbing the move is a rendering accommodation, never an intent, so it leaves
                // the desired column alone
                if (statesColumn && landed.col === clamped.col) {
                    desiredColumnRef.current = clamped.col;
                }

                pendingRef.current = null;
                cursorRef.current = landed;
                applyTabStop();
                focusTarget(cell);
                return;
            }

            const bodyGridRow = clamped.row - current.headerRowCount;

            // header rows are never windowed, and a body row is only missing while virtualised, so anything
            // else is a target that can never mount: leave the cursor alone rather than wait for it
            if (bodyGridRow < 0 || !current.isVirtualised) {
                return;
            }

            pendingRef.current = clamped;
            current.scrollToIndex(current.recordIndexByGridRow?.[bodyGridRow] ?? bodyGridRow);
        },
        [latest, findCell, applyTabStop, focusTarget],
    );

    /** Rows per Page Up / Down, from the live geometry so it matches what the user can actually see. */
    const getPageRows = React.useCallback(
        (cell: HTMLElement): number => {
            const viewport = scrollRef.current?.clientHeight ?? 0;
            const rowHeight = cell.closest('tr')?.offsetHeight ?? 0;

            if (viewport === 0 || rowHeight === 0) {
                return FALLBACK_PAGE_ROWS;
            }

            return Math.max(1, Math.floor(viewport / rowHeight));
        },
        [scrollRef],
    );

    /** Enter / Space on a cell hands over to the control inside it. */
    const activate = React.useCallback(
        (cell: HTMLElement): boolean => {
            const isDetail = cell.hasAttribute(GRID_DETAIL_ATTRIBUTE);

            // a nested grid promotes one of its own cells to `tabindex="0"`, which matches FOCUSABLE_SELECTOR,
            // so an unscoped query would step past the panel's own controls straight into the inner grid
            const control = isDetail
                ? Array.from(cell.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).find(
                      (candidate) => candidate.closest(`[${GRID_OWNER_ATTRIBUTE}]`) === cell,
                  ) ?? null
                : cell.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

            if (control === null) {
                // a panel whose controls are all disabled still owns the key: Space must not scroll the
                // region out from under the user
                return isDetail;
            }

            focusTarget(control);

            // a menu trigger opens in one keystroke; a checkbox or input is only focused, so the next key press
            // reaches it natively rather than being consumed here. A detail cell is exempt: its first control
            // is caller content, so it is focused, never clicked.
            if (control.matches(OPENER_SELECTOR) && !isDetail) {
                control.click();
            }

            return true;
        },
        [focusTarget],
    );

    const onKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>): void => {
            if (!latest.current.enabled || event.defaultPrevented) {
                return;
            }

            const target = event.target as HTMLElement;
            const cell = target.closest<HTMLElement>(ANY_CELL_SELECTOR);

            // the NEAREST cell owns the key: a nested grid's cell, and anything inside it, is never ours
            if (cell === null || cell.getAttribute(GRID_OWNER_ATTRIBUTE) !== latest.current.ownerId) {
                return;
            }

            // Escape is the only key handled from inside a cell's control: it hands focus back to the cell
            if (event.key === 'Escape') {
                if (target !== cell) {
                    event.preventDefault();
                    focusTarget(cell);
                }

                return;
            }

            if (target !== cell) {
                return;
            }

            const { row, col } = readCursor(cell);
            const desired = desiredColumnRef.current;
            const isRowStart = event.ctrlKey || event.metaKey;
            const { headerRowCount: headers, bodyGridRowCount: rows, columnCount: columns } = latest.current;

            let next: GridCursor;

            switch (event.key) {
                case 'ArrowRight':
                    next = { row, col: col + 1 };
                    break;
                case 'ArrowLeft':
                    next = { row, col: col - 1 };
                    break;
                case 'ArrowDown':
                    next = { row: row + 1, col: desired };
                    break;
                case 'ArrowUp':
                    next = { row: row - 1, col: desired };
                    break;
                case 'Home':
                    next = isRowStart ? { row: 0, col: 0 } : { row, col: 0 };
                    break;
                case 'End':
                    next = isRowStart ? { row: headers + rows - 1, col: columns - 1 } : { row, col: columns - 1 };
                    break;
                case 'PageDown':
                    next = { row: row + getPageRows(cell), col: desired };
                    break;
                case 'PageUp':
                    next = { row: row - getPageRows(cell), col: desired };
                    break;
                case 'Enter':
                case ' ':
                case 'Spacebar':
                    if (activate(cell)) {
                        event.preventDefault();
                    }

                    return;
                default:
                    return;
            }

            event.preventDefault();
            moveTo(next, COLUMN_KEYS.has(event.key));
        },
        [latest, moveTo, getPageRows, activate, focusTarget],
    );

    /**
     * Keeps the cursor on whatever the user clicked or tabbed into, so arrows resume from there.
     *
     * Scoped to this owner's nearest ANCESTOR cell rather than to the nearest cell of any grid: focus landing
     * inside a nested table parks this grid's tab stop on the cell that contains it.
     */
    const onFocusCapture = React.useCallback(
        (event: React.FocusEvent<HTMLElement>): void => {
            if (!latest.current.enabled) {
                return;
            }

            const cell = (event.target as HTMLElement).closest<HTMLElement>(selector);

            if (cell === null) {
                return;
            }

            // the user reaching a cell supersedes a move still waiting for its row to mount
            pendingRef.current = null;

            const cursor = readCursor(cell);
            cursorRef.current = cursor;

            // a detail cell is one full-width cell standing in for a whole row, so landing on it states
            // nothing about which column the user wants
            if (!isMovingRef.current && !cell.hasAttribute(GRID_DETAIL_ATTRIBUTE)) {
                desiredColumnRef.current = cursor.col;
            }

            applyTabStop();
        },
        [latest, applyTabStop, selector],
    );

    return { enabled, onKeyDown, onFocusCapture };
};
